// A collapsible group of sidebar links.
//
// The marketing pages accumulated one flat entry at a time until campaigns,
// templates, senders, journeys and contacts sat as siblings of Passports and
// QR Codes, with no hint that they are one tool. Grouping them also gives
// journeys and contacts somewhere to live: both existed but were reachable
// only from a dashboard page nothing linked to.
//
// It opens itself when you are inside it, so following a link into the group
// never leaves you looking at a collapsed section containing the page you are
// on. Beyond that it remembers what you left it as.

import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

export default function SidebarSection({ label, icon, match, children }) {
  const location = useLocation();
  const contains = match.some((p) => location.pathname.startsWith(p));
  const [open, setOpen] = useState(contains);
  // Following a link into the group must not leave it shut over the current
  // page, so being inside always wins over a stale collapsed state.
  const expanded = open || contains;

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen(!expanded)}
        aria-expanded={expanded}
        className={`w-full group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
          contains && !expanded ? 'bg-gray-900 text-white' : 'text-gray-300 hover:bg-gray-700 hover:text-white'
        }`}
      >
        &nbsp;
        {icon}
        <span className="flex-1 text-left">{label}</span>
        <svg
          className={`h-4 w-4 text-gray-400 transition-transform ${expanded ? 'rotate-90' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="2"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
        </svg>
      </button>
      {expanded && (
        <div className="mt-1 space-y-1">
          {children}
        </div>
      )}
    </div>
  );
}

/** A link inside a section: indented, no icon, so the group reads as a unit. */
export function SidebarSubLink({ href, children, exact = false }) {
  const nonActive = 'text-gray-400 hover:bg-gray-700 hover:text-white group flex items-center pl-11 pr-2 py-1.5 text-sm rounded-md';
  const active = 'bg-gray-900 text-white group flex items-center pl-11 pr-2 py-1.5 text-sm font-medium rounded-md';
  return (
    <NavLink to={href} end={exact}>
      {({ isActive }) => <div className={isActive ? active : nonActive}>{children}</div>}
    </NavLink>
  );
}
